import { useCallback, useState } from 'react'

/**
 * Completing checkout. There is no success path — every checkout terminates
 * at the cart with an in-character reason. Modeled honestly (no `success`
 * flag that's never true), same discipline as Regretsit's useSubmit.
 */
export interface CheckoutState {
  pending: boolean
  reason: string | null
  degraded: boolean
  error: string | null
}

export function useCheckout() {
  const [state, setState] = useState<CheckoutState>({ pending: false, reason: null, degraded: false, error: null })

  const complete = useCallback(async (accessToken: string, title: string, price: number, email: string) => {
    setState({ pending: true, reason: null, degraded: false, error: null })
    try {
      const res = await fetch('/api/checkout', {
        method: 'POST',
        headers: { 'content-type': 'application/json', authorization: `Bearer ${accessToken}` },
        body: JSON.stringify({ title, price, email }),
      })
      const data = (await res.json().catch(() => null)) as
        | { terminated?: boolean; reason?: string; degraded?: boolean }
        | null

      if (!res.ok || !data?.reason) {
        setState({
          pending: false,
          reason: null,
          degraded: false,
          error: 'The cart could not be reached to tell you no. Try again shortly.',
        })
        return
      }

      setState({ pending: false, reason: data.reason, degraded: !!data.degraded, error: null })
    } catch {
      setState({ pending: false, reason: null, degraded: false, error: 'The cart could not be reached.' })
    }
  }, [])

  const dismiss = useCallback(() => setState({ pending: false, reason: null, degraded: false, error: null }), [])

  return { ...state, complete, dismiss }
}
